import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronDown, FiChevronUp } = FiIcons;

const FAQ = () => {
  const faqs = [
    {
      question: "Is my writing private - and is my voice model mine?",
      answer: "Yes. Your manuscripts are protected with end-to-end encryption and stored on secure servers, and we're GDPR compliant. You keep full ownership of everything you write, and your Author Voice DNA profile is private to your account - it is never shared or used to train models for anyone else."
    },
    {
      question: "How does Author Voice DNA actually sound like me?",
      answer: "You give it a sample of your writing, and our stylometric engine analyzes it across seven dimensions - sentence rhythm, vocabulary, punctuation habits, tone, pacing, and more - to build a private style profile. Every suggestion the AI makes is generated to match that profile, so new chapters read like you wrote them, not like generic AI."
    },
    {
      question: "Will it really keep my book KDP-compliant?",
      answer: "That's the point of live KDP Preflight. As you write, it checks trim size, margins, headings, table of contents, front/back matter, and metadata against KDP's requirements and flags issues with one-click fixes - so you catch problems before upload instead of getting your manuscript kicked back."
    },
    {
      question: "Can I import an existing draft, or do I start fresh?",
      answer: "Both. Import an existing manuscript as DOCX, EPUB, TXT, or PDF and keep writing, or start a blank project right in the editor. Your chapters and scenes stay organized either way."
    },
    {
      question: "What formats can I export?",
      answer: "Export clean EPUB, reflowable and print-ready PDF, DOCX, and KDP-ready files - all formatted to spec and ready to upload to Kindle Direct Publishing or share with your editor."
    },
    {
      question: "Do I own what I write? Can I use it commercially?",
      answer: "One hundred percent. Everything you create is yours, with full commercial rights. Publish and sell on Amazon KDP, IngramSpark, your own store - wherever you like - and keep all of your royalties."
    },
    {
      question: "Can I turn my finished book into an audiobook?",
      answer: "Yes. The built-in Audiobook Studio narrates your finished manuscript chapter by chapter with AI, following ACX include/skip rules for which sections get read. It's an optional part of the workflow - ManuscriptSmith's core is writing and publishing - but you no longer need a separate tool to produce an audiobook."
    },
    {
      question: "What audiobook formats do I get, and are the files ACX-ready?",
      answer: "You get audiobook-ready files exported as MP3 and M4B, plus a retail sample. Every chapter is mastered to ACX/Audible technical spec - high-pass filtered, loudness normalized (around -20 LUFS integrated), peaks kept at or below -3 dB, and a noise floor at or below -60 dB - so the files meet the technical bar retailers check."
    },
    {
      question: "Can I upload the AI audio straight to Audible?",
      answer: "No - and we won't pretend otherwise. Audible/ACX only accepts AI narration through Amazon's own Virtual Voice on KDP, not external AI audio files. So we position the output as audiobook-ready files for Amazon Virtual Voice (via KDP) plus sanctioned wide distribution on platforms that permit it. Wide-catalog platforms like Spotify accept AI narration only through their own approved partners, and Google Play uses its own auto-narration. The Audiobook Studio helps you produce the master; where it can be sold depends on each retailer's AI policy."
    },
    {
      question: "Can I narrate in my own voice?",
      answer: "Not yet - today you pick from a library of natural AI narration voices and audition each one against your book before committing. What ManuscriptSmith does capture of you is your Voice DNA: a private fingerprint of how you write, built from your own work, so the words stay yours even when an AI narrator reads them."
    },
    {
      question: "How fast is audiobook turnaround?",
      answer: "Far faster than hiring a human narrator. Instead of the thousands of dollars and weeks of studio time a professional narration typically costs, AI narration generates your chapters and masters them to spec in a single automated pass, so you can go from finished manuscript to a mastered audiobook draft without the traditional wait."
    },
    {
      question: "What does it cost?",
      answer: "Plans start with Author at $19/mo. Paid plans unlock unlimited books, full export, Author Voice DNA, and live KDP Preflight - see the Pricing section for the current plans."
    }
  ];

  // State to track which FAQ items are expanded
  const [expandedItems, setExpandedItems] = useState({});

  const toggleItem = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="faq" className="py-20 bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Frequently{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
              Asked
            </span>{' '}
            Questions
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Everything you need to know about ManuscriptSmith
          </motion.p>
        </div>

        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
            >
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
                onClick={() => toggleItem(index)}
              >
                <span className="text-lg font-medium text-gray-900">{faq.question}</span>
                <SafeIcon
                  icon={expandedItems[index] ? FiChevronUp : FiChevronDown}
                  className="w-5 h-5 text-gray-500"
                />
              </button>
              {expandedItems[index] && (
                <div className="px-6 pb-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </motion.div>

        {/* Additional Support CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <p className="text-gray-600 mb-4">
            Can't find the answer you're looking for?
          </p>
          <button
            onClick={scrollToContact}
            className="inline-flex items-center px-5 py-3 bg-primary-50 text-primary-700 font-medium rounded-lg hover:bg-primary-100 transition-colors"
          >
            Contact our support team
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-2">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;